import { FlyoutListMenuWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { Icons } from '~commonInfra/components/Icons';
import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ExplorerDropdown } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerFlyoutPanel';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { RenameRelayPlanDialogOpts } from '../components/RenameRelayPlanDialog';
import { deleteRelayPlans } from '../utils/relayPlanActions';

export const ContentRelayPlanActionsMenuTestId = 'relay-plan-details-actions-menu';

type PlanAction = 'runNow' | 'rename' | 'delete';

export interface ContentRelayPlanActionsMenuProps {
  readonly luid: string;
  readonly name: string;
  readonly onRenamed: (name: string) => void;
  readonly onDeleted: () => void;
  readonly onRunNow: () => void;
}

/** Confirms, then deletes the plan; resolves once deleted (rejects if dismissed or the delete fails). */
export function confirmAndDeleteRelayPlan(luid: string): Promise<void> {
  return openReactConfirmationDialog<{ luids: string[] }, void>({
    title: Messages.relayPlanDeleteTitle({ COUNT: 1 }),
    message: Messages.relayPlanDeleteConfirm({ COUNT: 1 }),
    actionName: Messages.delete_action(),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: { luids: [luid] },
    action: function(params) {
      return deleteRelayPlans(params.luids).then(function() {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.relayPlanDeletedToast({ COUNT: 1 }) });
      });
    },
  }).result;
}

/**
 * The plan's ⋯ menu beside its title. Pilot scope: Run Now, Rename, and Delete. Schedule / Share / Change Owner / Revision History are out of the pilot.
 */
export function ContentRelayPlanActionsMenu(props: ContentRelayPlanActionsMenuProps): JSX.Element {
  const menuItems: MenuItemInfo[] = [
    { displayValue: Messages.runNow(), value: 'runNow' },
    { isDivider: true },
    { displayValue: Messages.rename_action(), value: 'rename' },
    { displayValue: Messages.delete_action(), value: 'delete' },
  ];

  const onSelect = function(value: PlanAction): void {
    if (value === 'runNow') {
      props.onRunNow();
    } else if (value === 'rename') {
      ReactModalService.openCustom(RenameRelayPlanDialogOpts(props.luid, props.name, props.onRenamed));
    } else if (value === 'delete') {
      // A dismissed dialog rejects too; only a completed delete leaves the page.
      confirmAndDeleteRelayPlan(props.luid).then(props.onDeleted, function() { return undefined; });
    }
  };

  const renderPanel = function(panelProps: ExplorerFlyoutPanelProps): JSX.Element {
    return (
      <FlyoutListMenuWidget
        role={'menu'}
        anchorEl={panelProps.anchorEl}
        allowAutoPosition={true}
        menuItemInfos={menuItems}
        onSelect={function(value: PlanAction) {
          panelProps.onCloseRequest();
          onSelect(value);
        }}
        requestCloseAll={panelProps.onCloseRequest}
        testId={panelProps.testId + '-list'}
        toggleEl={panelProps.anchorEl}
      />
    );
  };

  return (
    <ExplorerDropdown
      buttonTitle={Messages.actions()}
      panelTitle={Messages.actions()}
      testId={ContentRelayPlanActionsMenuTestId}
      renderButtonContentOnly={true}
      renderButtonContent={function(showingOrActive: boolean) {
        return <Icons.actionIcon hovered={showingOrActive} style={{ verticalAlign: 'middle' }} />;
      }}
      renderPanel={renderPanel}
      buttonStyleOverrides={SpaceHeaderStyles.IconButtonStyleOverridesV2}
    />
  );
}
